// Expense reports (PRD §4.17, ER1–ER5, ER10–ER11; rev. 1.21, approved 2026-10-20). One form for
// employees and 1099s alike: lines with a receipt or a lost-receipt affidavit, coded to a project or
// an internal code, approved by an admin who isn't the submitter, then repaid by a separate payment
// that Tom makes from QuickBooks, never through payroll.
//
// These are the pure rules, shared by the API (which enforces them) and the screen (which shows them).

import { daysBetween, isIsoDate, type Iso } from "./dates";
import type { ExpenseCategory as ProjectCostCategory } from "./projects";

export type ExpensePaidBy = "personal" | "company-card";
export type ExpenseReportStatus = "draft" | "submitted" | "returned" | "approved" | "reimbursed" | "closed";

export const EXPENSE_STATUS_LABEL: Readonly<Record<ExpenseReportStatus, string>> = {
  draft: "Draft",
  submitted: "Submitted",
  returned: "Returned",
  approved: "Approved, awaiting payment",
  reimbursed: "Reimbursed",
  closed: "Approved, nothing to repay",
};

/** An admin list (ER10), each mapped to a project cost category (PE8) and a shared-cost category. */
export interface ExpenseReportCategory {
  id: string;
  name: string;
  active: boolean;
  /** Where the spend lands on a client project (PE8). */
  projectCategory: ProjectCostCategory;
  /** Where the spend lands on an internal code (3S001–3S003): a shared-cost category id (rev. 1.18). */
  sharedCategory: string;
  /** Miles × the year's IRS rate instead of an amount, and no receipt. */
  mileage?: boolean;
  /** Needs who attended. */
  meal?: boolean;
}

/** Seeded from the rows of the 2026 expense sheet (Chris, 2026-10-19). */
export const DEFAULT_EXPENSE_CATEGORIES: ExpenseReportCategory[] = [
  { id: "airfare", name: "Airfare", active: true, projectCategory: "travel", sharedCategory: "travel" },
  { id: "lodging", name: "Lodging", active: true, projectCategory: "travel", sharedCategory: "travel" },
  { id: "ground", name: "Ground transport", active: true, projectCategory: "travel", sharedCategory: "travel" },
  { id: "meals", name: "Meals", active: true, projectCategory: "travel", sharedCategory: "other", meal: true },
  { id: "mileage", name: "Mileage", active: true, projectCategory: "travel", sharedCategory: "travel", mileage: true },
  { id: "software", name: "Software", active: true, projectCategory: "software", sharedCategory: "shared-services" },
  { id: "office", name: "Office and supplies", active: true, projectCategory: "other", sharedCategory: "admin" },
  { id: "marketing", name: "Marketing", active: true, projectCategory: "other", sharedCategory: "other" },
  { id: "event", name: "Event", active: true, projectCategory: "event", sharedCategory: "other" },
  { id: "subcontractor", name: "Subcontractor", active: true, projectCategory: "subcontractor", sharedCategory: "other" },
  { id: "other", name: "Other", active: true, projectCategory: "other", sharedCategory: "other" },
];

/** The internal codes, whose lines draw down shared costs rather than a client project (ER7). */
export const INTERNAL_CODES: readonly string[] = ["3S001", "3S002", "3S003"];
export const isInternalCode = (code: string) => INTERNAL_CODES.includes(code.toUpperCase());

export interface MileageRate {
  from: Iso;
  rate: number;
}

/** Rates as stored: dated, or the first version's one-per-year entries, read as from 1 January. */
export function mileageRatesOf(raw: readonly ({ from?: unknown; year?: unknown; rate?: unknown } | null | undefined)[] | undefined): MileageRate[] {
  return (raw ?? [])
    .map((r) => ({ from: typeof r?.from === "string" ? r.from : Number.isInteger(r?.year) ? `${r!.year}-01-01` : "", rate: Number(r?.rate) }))
    .filter((r) => isIsoDate(r.from) && r.rate > 0)
    .sort((a, b) => a.from.localeCompare(b.from));
}

export interface ExpenseSettings {
  id: "expense-settings";
  categories: ExpenseReportCategory[];
  /**
   * The IRS standard mileage rate, effective from a date, entered by an admin (ER10). Dated rather
   * than yearly because the IRS sometimes changes it mid-year, as in July 2026 (Chris, 2026-10-21).
   */
  mileageRates: MileageRate[];
  /** People who hold the company card (only Eric, Chris 2026-10-19): their lines may be paid by it. */
  companyCardHolders: string[];
}

export const defaultExpenseSettings = (): ExpenseSettings => ({
  id: "expense-settings",
  categories: DEFAULT_EXPENSE_CATEGORIES.map((c) => ({ ...c })),
  mileageRates: [],
  companyCardHolders: [],
});

/** A signed statement standing in for a required receipt that can't be produced (ER2). */
export interface LostReceiptAffidavit {
  statement: string;
  signedBy: string;
  signedOid: string;
  signedAt: string;
}

export interface ExpenseLine {
  id: string;
  date: Iso;
  merchant: string;
  /** For mileage, miles × the year's rate, worked out by the app. */
  amount: number;
  miles?: number;
  categoryId: string;
  purpose: string;
  paidBy: ExpensePaidBy;
  /** A client project code or an internal code (3S001–3S003). */
  code: string;
  /** Billed to the client at cost (client codes only). */
  billable: boolean;
  attendees?: string;
  receiptId?: string;
  affidavit?: LostReceiptAffidavit;
  /** An approver's note when the line was returned. */
  returnNote?: string;
  /** The client expense report this billable line was billed on (ER14). */
  clientReportId?: string;
}

export interface ExpenseHistory {
  at: string;
  by: string;
  action: string;
  note?: string;
}

export interface ExpenseReport {
  id: string;
  /** Short, for the email tag [3S-FIN ER-<ref>]. */
  ref: string;
  personId: string;
  title: string;
  status: ExpenseReportStatus;
  lines: ExpenseLine[];
  createdAt: string;
  submittedAt?: string;
  decidedBy?: string;
  decidedAt?: string;
  decisionNote?: string;
  /** The separate payment, made by Tom from QuickBooks (ER4). */
  payment?: {
    total: number;
    emailedAt?: string;
    paidAt?: string;
    paidBy?: string;
    reply?: string;
    replyFrom?: string;
    replyAt?: string;
    needsReview?: boolean;
  };
  history: ExpenseHistory[];
}

const round2 = (n: number) => Math.round(n * 100) / 100;
export const reportTotal = (r: Pick<ExpenseReport, "lines">) => round2(r.lines.reduce((t, l) => t + l.amount, 0));
/** What the person is repaid: every line not on the company card. */
export const reimbursableTotal = (r: Pick<ExpenseReport, "lines">) => round2(r.lines.filter((l) => l.paidBy === "personal").reduce((t, l) => t + l.amount, 0));

export const RECEIPT_LINE_LIMIT = 20;
export const RECEIPT_REPORT_LIMIT = 50;
export const STALE_DAYS = 60;

/**
 * The receipt rule (Chris, 2026-10-19 and 2026-10-20): a receipt is required for any line over $20,
 * and for every line once the lines without a receipt on the report add up to more than $50.
 * Mileage needs none. Returns the ids of lines that need one.
 */
export function receiptsRequired(lines: readonly ExpenseLine[], categories: readonly ExpenseReportCategory[]): Set<string> {
  const mileage = new Set(categories.filter((c) => c.mileage).map((c) => c.id));
  const counted = lines.filter((l) => !mileage.has(l.categoryId));
  const without = counted.filter((l) => !l.receiptId);
  const overall = without.reduce((t, l) => t + l.amount, 0) > RECEIPT_REPORT_LIMIT + 1e-9;
  return new Set(counted.filter((l) => l.amount > RECEIPT_LINE_LIMIT + 1e-9 || overall).map((l) => l.id));
}

/** Lines that need a receipt and have neither one nor a signed affidavit: these block submitting. */
export function missingReceipts(lines: readonly ExpenseLine[], categories: readonly ExpenseReportCategory[]): string[] {
  const required = receiptsRequired(lines, categories);
  return lines.filter((l) => required.has(l.id) && !l.receiptId && !l.affidavit).map((l) => l.id);
}

/** What the person attests when a receipt is lost (ER2). */
export function affidavitStatement(line: Pick<ExpenseLine, "date" | "merchant" | "amount" | "purpose">, name: string): string {
  return (
    `I, ${name}, state that I incurred this business expense: ${line.merchant}, ${line.date}, $${line.amount.toFixed(2)}, for ${line.purpose || "the stated purpose"}. ` +
    "The receipt has been lost and can't be obtained, and I have not been and will not be reimbursed for this expense by anyone else."
  );
}

export interface ExpenseWarning {
  lineId: string;
  kind: "meal-attendees" | "stale" | "duplicate" | "closed-code";
  message: string;
}

/**
 * Checks that warn without blocking (ER2): a meal with no attendees, a line older than 60 days (the
 * IRS safe-harbor period for an accountable plan; CPA to confirm), a likely duplicate of another of
 * the person's lines, and a closed project code.
 */
export function expenseWarnings(
  report: Pick<ExpenseReport, "lines">,
  o: { today: Iso; categories: readonly ExpenseReportCategory[]; otherLines: readonly ExpenseLine[]; closedCodes: ReadonlySet<string> },
): ExpenseWarning[] {
  const out: ExpenseWarning[] = [];
  const meals = new Set(o.categories.filter((c) => c.meal).map((c) => c.id));
  const key = (l: ExpenseLine) => `${l.date}|${l.merchant.trim().toLowerCase()}|${l.amount.toFixed(2)}`;
  const seen = new Map<string, number>();
  for (const l of [...o.otherLines, ...report.lines]) seen.set(key(l), (seen.get(key(l)) ?? 0) + 1);
  for (const l of report.lines) {
    if (meals.has(l.categoryId) && !l.attendees?.trim()) out.push({ lineId: l.id, kind: "meal-attendees", message: "A meal should list who attended." });
    if (isIsoDate(l.date) && daysBetween(l.date, o.today) > STALE_DAYS) out.push({ lineId: l.id, kind: "stale", message: `More than ${STALE_DAYS} days old: report expenses within ${STALE_DAYS} days.` });
    if ((seen.get(key(l)) ?? 0) > 1) out.push({ lineId: l.id, kind: "duplicate", message: "Looks like a duplicate: the same date, merchant and amount appears more than once." });
    if (o.closedCodes.has(l.code.toUpperCase())) out.push({ lineId: l.id, kind: "closed-code", message: `${l.code} is closed.` });
  }
  return out;
}

/** The IRS mileage rate in effect on a day: the latest one from on or before it; null if none is. */
export function mileageRateFor(settings: Pick<ExpenseSettings, "mileageRates">, day: Iso): number | null {
  let rate: number | null = null;
  for (const r of mileageRatesOf(settings.mileageRates)) if (r.from <= day) rate = r.rate;
  return rate;
}

/** Validates lines as the person saves them; returns messages, empty when fine. */
export function validateLines(lines: readonly ExpenseLine[], o: { categories: readonly ExpenseReportCategory[]; validCodes: ReadonlySet<string>; cardHolder: boolean }): string[] {
  const errors: string[] = [];
  const cats = new Map(o.categories.map((c) => [c.id, c]));
  const ids = new Set<string>();
  if (lines.length > 200) errors.push("A report holds up to 200 lines.");
  for (const l of lines) {
    if (!l.id || ids.has(l.id)) errors.push("Every line needs its own id.");
    ids.add(l.id);
    if (!isIsoDate(l.date)) errors.push("Every line needs a date.");
    const cat = cats.get(l.categoryId);
    if (!cat) errors.push("Pick a category for every line.");
    if (!l.merchant?.trim() || l.merchant.length > 120) errors.push("Every line needs a merchant, up to 120 characters.");
    if (!(Number.isFinite(l.amount) && l.amount > 0 && l.amount <= 100_000)) errors.push("Every line needs an amount above $0.");
    if (cat?.mileage && !(Number.isFinite(l.miles) && (l.miles ?? 0) > 0 && (l.miles ?? 0) <= 5_000)) errors.push("A mileage line needs the miles driven.");
    if (l.purpose.length > 500) errors.push("A purpose is up to 500 characters.");
    if (!o.validCodes.has(l.code.toUpperCase())) errors.push(`${l.code || "A line"} isn't a project code on the Projects list.`);
    if (l.billable && isInternalCode(l.code)) errors.push("Internal codes (3S001–3S003) can't be billed to a client.");
    if (l.paidBy === "company-card" && !o.cardHolder) errors.push("Only a company card holder can mark a line paid by the company card.");
    if (l.paidBy !== "personal" && l.paidBy !== "company-card") errors.push("Say how each line was paid.");
    if ((l.attendees ?? "").length > 500) errors.push("Attendees are up to 500 characters.");
  }
  return [...new Set(errors)];
}

// --- Actual cost from approved lines (ER6–ER8, slice (c)) -----------------------------------------------

/** Statuses that count as spent: approved, whether or not it has been repaid yet. */
export const POSTED_STATUSES: ReadonlySet<ExpenseReportStatus> = new Set(["approved", "reimbursed", "closed"]);

/** One approved line as cost in a month. */
export interface PostedExpense {
  reportId: string;
  lineId: string;
  personId: string;
  code: string;
  /** The month it counts in: its own, or the first open month after a closed one (AC7). */
  month: string;
  /** Moved out of a closed month into this one. */
  late: boolean;
  date: Iso;
  amount: number;
  /** Passed through to the client at cost: never moves margin (ER8). */
  billable: boolean;
  internal: boolean;
  categoryId: string;
  /** Where it lands on a project (PE8) and, for internal codes, in shared costs. */
  projectCategory: string;
  sharedCategory: string;
}

const nextMonth = (m: string) => {
  const [y, mo] = m.split("-").map(Number);
  return mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, "0")}`;
};

/** Every approved line of every report, as cost in the month it counts in. */
export function postedExpenses(reports: readonly ExpenseReport[], categories: readonly ExpenseReportCategory[], closedMonths: ReadonlySet<string>): PostedExpense[] {
  const cats = new Map(categories.map((c) => [c.id, c]));
  const out: PostedExpense[] = [];
  for (const r of reports) {
    if (!POSTED_STATUSES.has(r.status)) continue;
    for (const l of r.lines) {
      let month = l.date.slice(0, 7);
      const own = month;
      while (closedMonths.has(month)) month = nextMonth(month);
      const cat = cats.get(l.categoryId);
      out.push({
        reportId: r.id,
        lineId: l.id,
        personId: r.personId,
        code: l.code.toUpperCase(),
        month,
        late: month !== own,
        date: l.date,
        amount: l.amount,
        billable: l.billable,
        internal: isInternalCode(l.code),
        categoryId: l.categoryId,
        projectCategory: cat?.projectCategory ?? "other",
        sharedCategory: cat?.sharedCategory ?? "other",
      });
    }
  }
  return out;
}

/**
 * Actual non-labor cost by project code, month and PE8 category: approved lines that aren't billed to
 * the client (billable lines pass through and never move margin) and aren't internal codes (those are
 * shared costs, ER7). A month listed here replaces the plan's costs for that project; others keep it.
 */
export function actualProjectCosts(posted: readonly PostedExpense[]): Map<string, Map<string, Map<string, number>>> {
  const out = new Map<string, Map<string, Map<string, number>>>();
  for (const p of posted) {
    if (p.billable || p.internal) continue;
    const byMonth = out.get(p.code) ?? new Map<string, Map<string, number>>();
    const byCat = byMonth.get(p.month) ?? new Map<string, number>();
    byCat.set(p.projectCategory, (byCat.get(p.projectCategory) ?? 0) + p.amount);
    byMonth.set(p.month, byCat);
    out.set(p.code, byMonth);
  }
  return out;
}

export const sumCategories = (byCat: ReadonlyMap<string, number> | undefined) => [...(byCat?.values() ?? [])].reduce((t, v) => t + v, 0);


// --- Client billing (ER14–ER15, slice (d)) ----------------------------------------------------------------

/** Flagged this many days after the expense date while unbilled; due by the second (Chris, 2026-10-20). */
export const BILL_FLAG_DAYS = 45;
export const BILL_DUE_DAYS = 90;

export type BillingFlag = "ok" | "flagged" | "overdue";
export function billingFlag(date: Iso, today: Iso): { age: number; flag: BillingFlag } {
  const age = daysBetween(date, today);
  return { age, flag: age > BILL_DUE_DAYS ? "overdue" : age >= BILL_FLAG_DAYS ? "flagged" : "ok" };
}

export type ClientReportStatus = "issued" | "invoiced" | "paid" | "withdrawn";
export const CLIENT_REPORT_STATUS_LABEL: Readonly<Record<ClientReportStatus, string>> = {
  issued: "Issued",
  invoiced: "Invoiced",
  paid: "Paid by the client",
  withdrawn: "Withdrawn",
};

/** A line as billed: a snapshot, so the report reads the same whatever changes later. */
export interface BilledLine {
  reportId: string;
  lineId: string;
  personId: string;
  personName: string;
  date: Iso;
  merchant: string;
  categoryId: string;
  categoryName: string;
  code: string;
  purpose: string;
  amount: number;
  receiptId?: string;
  affidavit?: LostReceiptAffidavit;
}

/** The expense report sent to a client with a QuickBooks invoice (ER14): approved billable lines at cost. */
export interface ClientExpenseReport {
  id: string;
  /** CER-2026-001: what the invoice and the client refer to. */
  number: string;
  clientName: string;
  codes: string[];
  from?: Iso;
  to?: Iso;
  invoiceNumber?: string;
  lines: BilledLine[];
  total: number;
  status: ClientReportStatus;
  issuedAt: string;
  issuedBy: string;
  invoicedAt?: Iso;
  paidAt?: Iso;
  history: ExpenseHistory[];
}
